import React, { FC, useMemo, useState } from 'react'
import styled from 'styled-components'

import Box from 'components/Box'
import Icon from 'components/Icon'
import IconButton from 'components/Buttons/IconButton'
import RightPanel from 'components/RightPanel'
import Text, { TextProps } from 'components/Text'
import Toast from 'components/Toast'

import useScopeModal from 'hooks/use-scope-modal'
import useCopyText from 'hooks/use-copy-text'
import useLeadDetail from 'hooks/use-lead-detail'
import useWorkFlowLookup from 'hooks/lookup/use-workflow-lookup'
import useContactInfo from 'hooks/use-contact-info'
import CallOptions from 'modules/Leads/LeadDetail/shared/CallOptions'
import { useLeadContactQueryClient } from 'queries/lead-contact'
import { updateLeadContact } from 'services/lead-contact'
import { LeadDetails } from 'types/global'
import { FETCHING_STATUS } from 'utils/constants'
import { formatName } from 'utils/generic'
import { ContactInfo } from 'store/types/lead'
import { useDialerClientActions } from 'modules/DialerClient/useDialerClientActions'

import PanelTitle from '../shared/PanelTitle'
import ContactDetailsForm from './ContactDetails'
import DeactivateContactDetails from './DeactivateContactDetails'
import DeleteContactDetails from './DeleteContactDetails'
import ReactivateContactDetails from './ReactivateContactDetails'
import { Actions, getBadge, getMenuOptions, mutateMenuOptions } from './helper'
import EmailLogOptions from '../../shared/EmailLogOptions'
import { MODAL_STACK_CONTACT_LIST } from '.'

type Props = {
  data: LeadDetails.ContactPerson
  isLastActiveContact?: boolean
  onClose: (refresh?: boolean) => void
}

const detailProps = {
  fontSize: 14,
  lineHeight: '22px',
}

const itemsContainerProps = {
  alignItems: 'center',
  display: 'flex',
  mb: 10,
}

const headingTextProps: Partial<TextProps> = {
  fontSize: 16,
  fontWeight: 'bold',
  lineHeight: '24px',
  mb: '8px',
}

const primaryIconProps = {
  color: 'orange1',
  icon: 'flag',
  ml: 25,
}

const sectionProps = {
  mb: 32,
}

const EmptyField = () => <Text {...detailProps}>-</Text>

const ViewContactDetails: FC<Props> = ({ data, isLastActiveContact, onClose }) => {
  const { contactTypeId, email, fname, isActive, isPrimary, lname, mobile, leadId, id, jobTitle } = data

  const { canUserEditLeadInfo } = useLeadDetail()
  const { isFetching, isLoading, refetch, data: contactData } = useContactInfo({ leadId })
  const { invalidateQuery } = useLeadContactQueryClient(leadId)

  const { contactTypes } = useWorkFlowLookup({})
  const [showEditForm, setShowEditForm] = useState(false)
  const [selectedContactInfoId, setSelectedContactInfoId] = useState<string>()
  const [isSubmitting, setSubmitting] = useState(false)
  const isBusy = isSubmitting || isFetching || isLoading

  const { setModal: setStackModal, setCloseModal: setStackCloseModal } = useScopeModal({
    stack: MODAL_STACK_CONTACT_LIST,
  })
  const { setModal } = useScopeModal()

  const { handleCopyText } = useCopyText(true)

  const { showRightPanel } = useDialerClientActions()

  const contactType = useMemo(() => contactTypes.find((i) => i.id === contactTypeId)?.name, [
    contactTypeId,
    contactTypes,
  ])

  const isStatusUpdatable = useMemo(() => {
    if (contactData) {
      return contactData?.filter((contact) => contact?.isActive)?.length > 1
    }
    return true
  }, [contactData])

  const menuOptions = useMemo(() => {
    if (canUserEditLeadInfo) return []
    let options = getMenuOptions(false, isActive, isPrimary)

    if (!isStatusUpdatable && isPrimary) {
      options = mutateMenuOptions(
        [Actions.deactivate, Actions.delete],
        { isDisabled: true, labelColor: undefined },
        options?.map((option) => ({ ...option })),
      )
    }

    return options
  }, [canUserEditLeadInfo, isActive, isPrimary, isStatusUpdatable])

  const handleCloseEditForm = (closePanel = false) => {
    if (closePanel) {
      onClose()
      return
    }

    setShowEditForm(false)
  }

  const handleMarkAsPrimary = async () => {
    const payload = {
      contact_id: id as string,
      contact_type_id: contactTypeId,
      email: email.map(({ contactInfoTypeId, id, isPrimary, value }: LeadDetails.Email) => ({
        contact_info_id: id,
        contact_info_type_id: contactInfoTypeId,
        is_primary: isPrimary,
        value,
      })),
      fname,
      job_title: jobTitle,
      lead_id: leadId,
      is_primary: true,
      lname,
      mobile: mobile.map(({ id, contactInfoTypeId, isPrimary, value }: LeadDetails.Mobile) => ({
        contact_info_id: id,
        contact_info_type_id: contactInfoTypeId,
        is_primary: isPrimary,
        value,
      })),
    }

    setSubmitting(true)
    const response = await updateLeadContact(payload)

    setSubmitting(false)
    if (response.status === FETCHING_STATUS.fail) {
      Toast.toastMessage({
        message: `lead_contact_set_primary_error`,
        variant: 'error',
      })
      return
    }

    Toast.toastMessage({
      message: `lead_contact_set_primary_success`,
      variant: 'success',
    })

    invalidateQuery()
  }

  const handleContextMenuOptionClick = (option: keyof typeof Actions) => {
    if (!leadId) {
      return
    }

    if ((option === Actions.deactivate || option === Actions.delete) && !isStatusUpdatable) {
      Toast.toastMessage({
        message: `unable_to_delete_or_deactivate_primary_contact`,
        variant: 'error',
      })
      return
    }

    switch (option) {
      case Actions.markAsPrimary:
        handleMarkAsPrimary()
        break
      case Actions.deactivate:
        setModal(
          <DeactivateContactDetails contactId={id} isActive={isActive} onAction={handleConfirmationModalAction} />,
        )
        break
      case Actions.reactivate:
        setModal(<ReactivateContactDetails contactId={id} leadId={leadId} onAction={handleConfirmationModalAction} />)
        break
      case Actions.delete:
        setModal(<DeleteContactDetails contactId={id} onAction={handleConfirmationModalAction} />)
        break
      default:
    }
  }

  const handleConfirmationModalAction = (success?: boolean) => {
    if (!leadId) {
      return
    }

    setModal(null)

    if (success) {
      refetch()
      onClose(true)
      return
    }
  }

  const resolveContactInfo = (mobile: LeadDetails.Mobile): ContactInfo | undefined =>
    mobile.id
      ? {
          ...mobile,
          contactInfoId: mobile.id,
          contactValue: mobile.value,
          fname,
          isOtherNumber: false,
          isPrimaryContact: mobile.isPrimary,
          lname,
          otherContactInfoTypeId: '',
          otherContactValue: '',
        }
      : undefined

  if (showEditForm) {
    return <ContactDetailsForm data={data} onClose={handleCloseEditForm} />
  }

  return (
    <RightPanel
      disableSubmit={!canUserEditLeadInfo}
      onCancel={() => onClose(false)}
      onSubmit={() => {
        setStackModal(
          <ContactDetailsForm
            data={data}
            isLastActiveContact={isLastActiveContact}
            onClose={() => setStackCloseModal('lead-detail-edit-contact-details')}
          />,
          { id: 'lead-detail-edit-contact-details' },
        )
        onClose(false)
      }}
      submitIcon={['fal', 'pencil']}
      submitIconColor="gray5"
      submitLabel="edit_details"
      submitVariant="outline"
      isSubmitting={isBusy}
      title={
        <PanelTitle<keyof typeof Actions>
          badge={getBadge(isActive, isPrimary)}
          menuOptions={menuOptions}
          onClickOption={handleContextMenuOptionClick}
          title="contact_details_lbl"
          showMenu={canUserEditLeadInfo && !isLastActiveContact}
        />
      }
      width={532}
      closeOnClickOutside
      isShowFooter={canUserEditLeadInfo}
    >
      <Box {...sectionProps}>
        <Text {...headingTextProps}>name_lbl</Text>
        <Box {...itemsContainerProps}>
          <Text {...detailProps}>{formatName(fname, lname)}</Text>
          <IconButton
            icon={['far', 'copy']}
            ml="10px"
            variant="outline"
            onClick={() => handleCopyText(formatName(fname, lname))}
          />
        </Box>
      </Box>
      <Box {...sectionProps}>
        <Text {...headingTextProps}>email_address_lbl</Text>
        {email.length === 0 && <EmptyField />}
        {email.length > 0 &&
          email.map((i, index) => (
            <Box {...itemsContainerProps} key={index}>
              <EmailLogOptions
                id={id}
                recipient={i}
                onSelect={() => onClose(false)}
                isPortaled={false}
                isDisabled={!canUserEditLeadInfo}
              >
                <StyledIconButton
                  icon={['far', 'envelope']}
                  disabled={!canUserEditLeadInfo}
                  width={32}
                  height={32}
                  iconSize={16}
                  mr={16}
                />
              </EmailLogOptions>
              <Text {...detailProps}>{i.value}</Text>
              <IconButton icon={['far', 'copy']} ml="10px" variant="outline" onClick={() => handleCopyText(i.value)} />
              {i.isPrimary && <Icon {...primaryIconProps} />}
            </Box>
          ))}
      </Box>
      <Box {...sectionProps}>
        <Text {...headingTextProps}>mobile_number_lbl</Text>
        {mobile.length === 0 && <EmptyField />}
        {mobile.length > 0 &&
          mobile.map((i, index) => {
            const contactInfo = resolveContactInfo(i)

            return (
              <React.Fragment key={index}>
                <Box
                  {...itemsContainerProps}
                  key={index}
                  mb={selectedContactInfoId === i.id ? undefined : itemsContainerProps.mb}
                >
                  <CallOptions
                    contactInfo={contactInfo}
                    onSelectCallLog={() => onClose(false)}
                    onSelectLiveCall={() => {
                      showRightPanel(contactInfo)
                      onClose(false)
                    }}
                    isPortaled={false}
                    isDisabled={!canUserEditLeadInfo}
                  >
                    <StyledIconButton
                      disabled={!canUserEditLeadInfo}
                      icon={['far', 'phone']}
                      iconSize={16}
                      width={32}
                      height={32}
                      mr={16}
                      onClick={() => setSelectedContactInfoId(i.id)}
                    />
                  </CallOptions>
                  <Text {...detailProps}>{i.value}</Text>
                  <IconButton
                    icon={['far', 'copy']}
                    ml="10px"
                    variant="outline"
                    onClick={() => handleCopyText(i.value)}
                  />
                  {i.isPrimary && <Icon {...primaryIconProps} />}
                </Box>
              </React.Fragment>
            )
          })}
      </Box>
      <Box {...sectionProps}>
        <Text {...headingTextProps}>contact_type_lbl</Text>
        {contactType && <Text {...detailProps}>{contactType}</Text>}
        {!contactType && <EmptyField />}
      </Box>
    </RightPanel>
  )
}

const StyledIconButton = styled(IconButton)`
  border-color: ${(p) => p.theme.colors.gray4};
  padding: 8px 3px 6px;
  position: relative;

  & > svg {
    color: ${(p) => p.theme.colors.gray5};
  }
`

export default ViewContactDetails
